import {
	AfterViewInit,
	Component,
	DestroyRef,
	inject,
	Input,
	ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import {
	MatSlideToggleChange,
	MatSlideToggle,
} from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { UserProfile } from 'src/app/models/model';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { Client } from 'src/app/models/client';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { ClientDetailsService } from '../../../services/client-details.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetRowClickDirective,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { AddressPipe } from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-client-list-table',
	templateUrl: './client-list-table.component.html',
	styleUrls: ['./client-list-table.component.scss', '../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		MatSlideToggle,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatChipSet,
		MatChip,
		NgClass,
		MatIcon,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		AddressPipe,
		DataSetLoadingRowComponent,
	],
})
export class ClientListTableComponent implements AfterViewInit {
	@Input() isUnitTesting = false; // Do NOT set this variable to true! Only used for unit tests.
	@Input() propertyContext?: { locationId: string; propertyCompanyId?: string }; // Optional: filter clients by property and check ownership
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns = [
		'name',
		'idn',
		'propertyName',
		'address',
		'status',
		'serialNumber',
		'numberOfCompanies',
		'numberOfContacts',
		'deviceSource',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<Client>();
	activeCompanyId: string | undefined = '';
	showInactive: boolean = false;
	canAddClients: boolean = false;

	filterLabels = {
		defaultPlaceholder: $localize`:@@clientListTable-defaultPlaceholder:Search client name...`,
		clientName: $localize`:@@clientListTable-filterLabelClientNameLabel:Client Name`,
		idn: $localize`:@@clientListTable-filterLabelIDNLabel:Client IDN`,
		propertyName: $localize`:@@clientListTable-filterLabelPropertyNameLabel:Property Name`,
		address: $localize`:@@clientListTable-filterLabelAddressLabel:Address`,
		serialNumber: $localize`:@@clientListTable-filterLabelSerialNumberLabel:Serial Number`,
	};

	finishedLoadingInitData: boolean = false;

	get getClientsLoadingKey(): string {
		return this.propertyContext?.locationId
			? ApiLoadingKeys.getClientsByLocationId
			: ApiLoadingKeys.getClients;
	}

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly userSessionService: UserSessionService,
		private readonly scService: SystemCapabilityService,
		private readonly clientDetailsService: ClientDetailsService,
		public readonly loadingService: LoadingIndicatorService
	) {}

	async ngAfterViewInit(): Promise<void> {
		await this.setActiveCompanyId();

		if (!this.activeCompanyId) {
			this.finishedLoadingInitData = true;
			return;
		}

		this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					try {
						// Always send showInactive parameter
						const clientFilter = [
							...filter,
							{ param: 'showInactive', value: this.showInactive.toString() },
						];

						// If propertyContext is provided, get clients by location; otherwise get clients by company
						const result = this.propertyContext?.locationId
							? await this.apiService.getClientsByLocationId(
									this.propertyContext.locationId,
									clientFilter
								)
							: await this.apiService.getClients(
									this.activeCompanyId!,
									clientFilter
								);
						this.finishedLoadingInitData = true;
						return result;
					} catch (error) {
						this.finishedLoadingInitData = true;
						return [];
					}
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});
	}

	onToggleInactive(event: MatSlideToggleChange): void {
		this.showInactive = event.checked;
		// Trigger data refresh
		this.dataSet.applyFilters();
	}

	onAddButtonClick(): void {
		if (this.propertyContext?.locationId) {
			this.router.navigate(['/add-client'], {
				queryParams: { locationId: this.propertyContext.locationId },
			});
		} else {
			this.router.navigateByUrl('/add-client');
		}
	}

	onRowClick(client: Client): void {
		this.clientDetailsService.saveClientDetails(client);
		this.router.navigate(['/clients', client.id]);
	}

	async setActiveCompanyId(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		// Only care companies that own devices AND have property management capability can add clients
		if (this.activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(
				this.activeCompanyId
			);
			const isCareCompany = companyData?.company?.companyType === 'care';
			const companyOwnsDevices = await this.apiService.getCompanyOwnsDevices(
				this.activeCompanyId
			);
			const hasPropertyManagement = this.scService.has(
				SystemCapabilityEnum.propertyManagement
			);

			// If viewing clients at a specific property, also check if the company owns that property,
			// if they don't own the property, disallow adding clients
			const ownsProperty = this.propertyContext?.propertyCompanyId
				? this.activeCompanyId === this.propertyContext?.propertyCompanyId
				: true;

			this.canAddClients =
				isCareCompany &&
				companyOwnsDevices &&
				hasPropertyManagement &&
				ownsProperty;
		}
	}

	rowId(row: Client): string {
		return row?.id || 'unknown-id';
	}

	isOwnedDevice(client: Client): boolean {
		const activeCompanyId =
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId;
		return (
			client.deviceSource !== '- - -' &&
			client.deviceOwnerCompanyId === activeCompanyId
		);
	}
}
